import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  ShareDropDownItemButton,
  type ShareDropDownItemButtonProps,
} from '~/components/features/ShareDropDownItemButton'
import { useCopyToClipboard } from '~/hooks/useCopyToClipboard'
import { useCreateSharedUrl } from '~/hooks/useCreateSharedUrl'
import { useLogEvent } from '~/hooks/useLogEvent'
import { type ColumnInfo, type FigureInfo, type RowInfo } from '~/utils'
import { convertTableToClipboardData, exists } from '~/utils'

import { composeTableForCopy } from './utils'

export type CopyToClipboardButtonProps<ColumnKey extends string> = Pick<
  ShareDropDownItemButtonProps,
  'disabled'
> & {
  columns: ColumnInfo<ColumnKey>[]
  'data-testid'?: string
  figureInfo: FigureInfo
  restoringParams: unknown
  tableInfo: RowInfo<ColumnKey>[]
}

export const CopyToClipboardButton = <ColumnKey extends string>({
  columns,
  'data-testid': dataTestId = 'components-features-copy-to-clipboard-button',
  disabled,
  figureInfo,
  restoringParams,
  tableInfo,
}: CopyToClipboardButtonProps<ColumnKey>) => {
  const { t } = useTranslation()
  const logEvent = useLogEvent()

  const createSharedUrl = useCreateSharedUrl()
  const copyToClipboard = useCopyToClipboard()

  const handleClick = React.useCallback<
    EH<ShareDropDownItemButtonProps, 'onClick'>
  >(async () => {
    const result = await createSharedUrl(restoringParams, {
      notCopyToClipboard: true,
    })

    if (!exists(result)) {
      return
    }

    const tableForCopy = tableInfo
      .map((tableCellInfo) => columns.map(({ key }) => tableCellInfo[key]))
      .map((rowInfo) =>
        rowInfo.map((cellInfo) => cellInfo?.copyValue ?? cellInfo?.text ?? ''),
      )

    const compositeTable = composeTableForCopy({
      figureInfo,
      sharedUrl: result.sharedUrl,
      tableForCopy,
      t,
    })
    const dataForCopy = convertTableToClipboardData(compositeTable)

    copyToClipboard(dataForCopy)

    logEvent('Copy table', { withSuffix: true })
  }, [
    columns,
    copyToClipboard,
    createSharedUrl,
    figureInfo,
    logEvent,
    restoringParams,
    t,
    tableInfo,
  ])

  return (
    <ShareDropDownItemButton
      data-testid={dataTestId}
      disabled={disabled}
      label={t('copy_to_clipboard')}
      onClick={handleClick}
    />
  )
}
